import { useEffect, useRef, useState } from 'react';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

let activityListenersAttached = false;
let lastActivity = Date.now();
const subscribers = new Set();

function attachListenersOnce() {
  if (activityListenersAttached || typeof window === 'undefined') return;
  activityListenersAttached = true;

  const onActivity = () => {
    lastActivity = Date.now();
    subscribers.forEach(fn => fn(true));
  };

  ACTIVITY_EVENTS.forEach(ev => {
    window.addEventListener(ev, onActivity, { passive: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) onActivity();
  });

  // Tick every 30s to flip subscribers to idle when timeout elapses
  setInterval(() => {
    const idle = Date.now() - lastActivity > IDLE_TIMEOUT_MS;
    subscribers.forEach(fn => fn(!idle));
  }, 30 * 1000);
}

/**
 * Returns the polling interval to pass to React Query's `refetchInterval`,
 * or `false` to pause polling. Pauses when:
 *   - User has been idle (no mouse/keyboard/touch) for 5 minutes
 *   - `enabled` is false (e.g. no data to poll for)
 *
 * @param {number} intervalMs - desired polling interval in ms
 * @param {boolean} enabled - whether polling should run at all (default true)
 * @returns {number|false}
 */
export function useActivePolling(intervalMs, enabled = true) {
  const [isActive, setIsActive] = useState(true);
  const cbRef = useRef();

  useEffect(() => {
    attachListenersOnce();
    cbRef.current = setIsActive;
    subscribers.add(setIsActive);
    return () => subscribers.delete(setIsActive);
  }, []);

  if (!enabled) return false;
  if (!isActive) return false;
  return intervalMs;
}
